import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminGet, adminPut, adminPost, adminDel } from "@/lib/adminApi";
import WeekGrid from "@/components/admin/WeekGrid";
import ClientFiche from "@/components/admin/ClientFiche";

const STATUSES = ["demandé", "en attente de confirmation", "confirmé", "à venir", "en cours", "terminé", "annulé utilisateur", "annulé RAV", "absent", "reporté"];

const FR_DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function frDate(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${FR_DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

export default function Rdv() {
  const [rdv, setRdv] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [kind, setKind] = useState("dispo");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [label, setLabel] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [fiche, setFiche] = useState(null); // {detail} ou {booking}
  const navigate = useNavigate();

  // Cliente avec compte → fiche en pleine page ; cliente locale sans compte → fiche rapide en ligne
  const openBooking = (booking) => {
    if (booking.user_id) {
      navigate(`/admin/patientele/${booking.user_id}`);
    } else {
      setFiche({ booking });
    }
  };

  const load = () => {
    adminGet("/rdv").then((d) => setRdv(d.rdv)).catch(() => {});
    adminGet("/planning").then((d) => setPlanning(d.planning)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (r, status) => {
    await adminPut(`/rdv/${r.id}`, { status }).catch(() => {});
    load();
  };

  const addPlage = async () => {
    const effStart = kind === "bloque" && allDay ? "00:00" : start;
    const effEnd = kind === "bloque" && allDay ? "23:59" : end;
    if (!date || !effStart || !effEnd) {
      toast("Choisissez la date, l'heure de début et l'heure de fin.");
      return;
    }
    try {
      await adminPost("/planning", { kind, date, start: effStart, end: effEnd, label });
      toast(kind === "dispo" ? "Disponibilité ajoutée au planning." : kind === "occupe" ? "Rendez-vous local ajouté au planning." : "Temps indisponible bloqué.");
      setLabel("");
      setAllDay(false);
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Vérifiez la date et les horaires.");
    }
  };

  const removePlage = async (p) => {
    await adminDel(`/planning/${p.id}`).catch(() => {});
    load();
  };

  return (
    <div data-testid="admin-rdv-module">
      <h2 className="rav-admin-h2">Rendez-vous</h2>
      {rdv.length === 0 && <p className="rav-page-sub">Aucun rendez-vous pour le moment.</p>}
      <div className="rav-admin-list">
        {rdv.map((r, i) => (
          <div key={r.id} className="rav-admin-row" data-testid={`rdv-row-${i}`}>
            <span className="rav-admin-row-label">
              {r.slot_label}
              {r.price != null ? ` — ${r.price} €` : ""}
            </span>
            <span className="rav-admin-row-sub">{[r.name, r.email, r.reason].filter(Boolean).join(" · ") || "Utilisatrice"}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`rdv-visio-${i}`} onClick={() => window.open(`/visio/${r.id}`, "_blank")}>
                Ouvrir la visio
              </button>
              <select className="rav-input rav-admin-select" data-testid={`rdv-status-${i}`} value={r.status} onChange={(e) => setStatus(r, e.target.value)}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </span>
          </div>
        ))}
      </div>

      <h2 className="rav-admin-h2">Planning de Julie</h2>
      <p className="rav-page-sub rav-admin-note">
        Un seul planning pour tout : ouvrez vos disponibilités visio (« Disponible ») et bloquez les rendez-vous de votre
        clientèle locale (« Occupé »). Le menu « Choisissez votre créneau… » du site suit ce planning automatiquement.
      </p>
      <WeekGrid planning={planning} rdv={rdv} onSelectBooking={openBooking} />
      {fiche && <ClientFiche detail={fiche.detail} booking={fiche.booking} onClose={() => setFiche(null)} />}
      <div className="rav-admin-form" data-testid="planning-form">
        <div className="rav-admin-form-row rav-planning-kinds">
          {[
            { id: "dispo", label: "Disponible", hint: "réservable en ligne" },
            { id: "occupe", label: "Occupé", hint: "clientèle locale" },
            { id: "bloque", label: "Indisponible", hint: "ni visio ni local" },
          ].map((k) => (
            <button
              key={k.id}
              type="button"
              className={`rav-chip${kind === k.id ? " is-selected" : ""}`}
              data-testid={`planning-kind-${k.id}`}
              onClick={() => setKind(k.id)}
            >
              {k.label} · {k.hint}
            </button>
          ))}
        </div>
        <div className="rav-admin-form-row">
          <input className="rav-input" data-testid="planning-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          {kind === "bloque" && (
            <label className="rav-field rav-admin-check">
              <input type="checkbox" data-testid="planning-allday" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
              <span>Toute la journée</span>
            </label>
          )}
        </div>
        {!(kind === "bloque" && allDay) && (
          <div className="rav-admin-form-row">
            <input className="rav-input" data-testid="planning-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            <input className="rav-input" data-testid="planning-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        )}
        {(kind === "occupe" || kind === "bloque") && (
          <input
            className="rav-input"
            data-testid="planning-label"
            placeholder={kind === "occupe" ? "Note (ex. Mme Martin — cabinet)" : "Note (ex. Courses, famille, repos…)"}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        )}
        <button type="button" className="rav-secondary-button rav-session-btn rav-admin-create" data-testid="planning-add" onClick={addPlage}>
          {kind === "bloque" ? "Bloquer ce temps" : "Ajouter au planning"}
        </button>
      </div>
      <div className="rav-admin-list">
        {planning.length === 0 && <p className="rav-page-sub">Le planning est vide — ajoutez votre première plage.</p>}
        {planning.map((p, i) => (
          <div key={p.id} className="rav-admin-row" data-testid={`planning-row-${i}`}>
            <span className="rav-admin-row-label">
              {p.kind === "dispo" ? "Disponible" : p.kind === "occupe" ? "Occupé" : "Indisponible"} — {frDate(p.date)} ·{" "}
              {p.start === "00:00" && p.end === "23:59" ? "toute la journée" : `${p.start.replace(":", "h")} → ${p.end.replace(":", "h")}`}
            </span>
            {p.label && <span className="rav-admin-row-sub">{p.label}</span>}
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`planning-delete-${i}`} onClick={() => removePlage(p)}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
